import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { PageOrder } from '../components/page/pageOrder';
import { AppContext } from '../containers/appContextContainer';
import {
    InntektContainerWeb,
    InntektDeleteWeb,
    InntektUpdateWeb,
    LeieinntekterWeb,
    SoeknadstypeWeb,
    VedleggDeleteWeb,
    VedleggWeb,
} from '../generated-types';
import errorTypes from '../util/gtm/errorTypes';
import restClient from '../util/web-http-client';
import useFeatureToggle from './useFeatureToggle';

export const useInntekt = (soeknadtype: SoeknadstypeWeb) => {
    const { setError } = useContext(AppContext);
    const { setPageOrder } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['inntekt'],
        queryFn: () => restClient.getInntekt(soeknadtype),
        retry: 1,
    });
    const { data: featureToggles } = useFeatureToggle();

    useEffect(() => {
        if (query.data) {
            if (
                soeknadtype === SoeknadstypeWeb.MEDLAANTAKER &&
                (query.data.soeknadtype === SoeknadstypeWeb.FINANSIERINGSBEVIS ||
                    query.data.soeknadtype === SoeknadstypeWeb.KJOEPE_BOLIG)
            ) {
                setPageOrder(PageOrder(SoeknadstypeWeb.MEDLAANTAKER_EGENKAPITAL, featureToggles?.utvideDatagrunnlag));
            }
        }
        if (query.error) {
            setError({
                errorType: errorTypes.GET_INNTEKT,
                errorException: query.error as RequestExceptionWithResponse,
            });
        }
    }, [query.error, query.data, soeknadtype, setPageOrder, setError, featureToggles?.utvideDatagrunnlag]);

    return query;
};

export const useUpdateInntekt = (
    inntektContainerWeb: InntektContainerWeb | undefined,
    successCallback?: () => void,
) => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['updateInntekt'],
        mutationFn: (inntektUpdateWeb: InntektUpdateWeb) => restClient.updateInntekt(inntektUpdateWeb),
        onSuccess: (data: InntektUpdateWeb) => {
            if (inntektContainerWeb) {
                inntektContainerWeb.inntekt = data.inntekt;
                inntektContainerWeb.harInntekt = data.harInntekt;
                queryClient.setQueryData(['inntekt'], inntektContainerWeb);
            }
            if (successCallback) {
                successCallback();
            }
        },
        onError: (error: unknown) => {
            setError({ errorType: errorTypes.UPDATE_INNTEKT, errorException: error as RequestExceptionWithResponse });
        },
    });
};

export const useDeleteInntekt = () => {
    const { setError } = useContext(AppContext);
    return useMutation({
        mutationKey: ['deleteInntekt'],
        mutationFn: (inntektDelete: InntektDeleteWeb) => restClient.deleteInntekt(inntektDelete),
        onError: (error: unknown) => {
            setError({ errorType: errorTypes.DELETE_INNTEKT, errorException: error as RequestExceptionWithResponse });
        },
    });
};

export const useLeieinntekter = (soeknadtype: SoeknadstypeWeb) => {
    const { setError } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['leieinntekter'],
        queryFn: () => restClient.getLeieinntekter(soeknadtype),
        refetchOnMount: 'always',
        retry: 1,
    });

    useEffect(() => {
        if (query.error) {
            setError({
                errorType: errorTypes.GET_LEIEINNTEKT,
                errorException: query.error as RequestExceptionWithResponse,
            });
        }
    }, [query.error, setError]);

    return query;
};

export const useLagreLeieinntekter = (successCallback?: () => void) => {
    const { setError } = useContext(AppContext);
    return useMutation({
        mutationKey: ['lagreLeieinntekter'],
        mutationFn: (leieinntekterFormData: FormData) => restClient.lagreLeieinntekter(leieinntekterFormData),
        onSuccess: () => {
            if (successCallback) {
                successCallback();
            }
        },
        onError: (error: unknown) => {
            setError({
                errorType: errorTypes.UPDATE_LEIEINNTEKT,
                errorException: error as RequestExceptionWithResponse,
            });
        },
    });
};

export const useDeleteLeiekontrakt = () => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['deleteLeiekontrakt'],
        mutationFn: (vedleggDelete: VedleggDeleteWeb) => restClient.deleteVedlegg(vedleggDelete),
        onSuccess: (data: VedleggDeleteWeb) => {
            const leieinntekter = queryClient.getQueryData<LeieinntekterWeb>(['leieinntekter']);
            leieinntekter?.leiekontrakt.splice(
                leieinntekter?.leiekontrakt.findIndex((v: VedleggWeb) => v.dokumentNavn === data.dokumentNavn),
                1,
            );
            queryClient.setQueryData(['leieinntekter'], leieinntekter);
        },
        onError: (error: unknown) => {
            setError({ errorType: errorTypes.DELETE_VEDLEGG, errorException: error as RequestExceptionWithResponse });
        },
    });
};
